import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { Bilgi } from '../satis-analiz/Bilgi';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import { TiklaAc } from '../satis-analiz/TiklaAc';
import { oranMetni } from '../satis-analiz/bicim';
import type { Donem } from '../satis-analiz/donem';
import type { StokKaynaklari } from './ekranKaynaklari';
import { useStokSorgu, type StokKaynak, type StokSorguFiltresi } from './useStokVerisi';
import { kovalariBirlestir, kategoriyeTopla, type KovaSatiri } from './toplama';
import { kategoriNormalize } from './kategori';
import { kapsamNotuGorunsunMu } from './kapsam';
import { fmtCarpan } from './bicimYardimcilari';
import { KartHatasi } from './KartHatasi';
import type { StokDetay } from './detay/stokDetayTipleri';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube?: string | null;
  ac?: (d: StokDetay) => void;
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 1 — Stok yaşlandırma (990043) — kovalariBirlestir ile DÖRT kova
// ════════════════════════════════════════════════════════════════════════════════════════════

function YaslandirmaKarti({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  const q = useStokSorgu<KovaSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-yaslandirma-iskelet" baslik="Stok yaşlandırma" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return (
      <KartHatasi testid="sermaye-yaslandirma-hata" baslik="Stok yaşlandırma" yukseklik={200} mesaj="Stok yaşlandırma yüklenemedi." />
    );
  }
  if (q.data === undefined) return null;
  const kovalar = kovalariBirlestir(q.data.data);
  if (kovalar.length === 0) return null;

  return (
    <div data-testid="sermaye-yaslandirma" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Stok yaşlandırma"
        subtitle="Parti sayısı ve tutar — kova bazında (anlık, bugün)"
      >
        <ul className="flex flex-col gap-2">
          {kovalar.map((k) => {
            const icerik = (
              <>
                <span className="text-ink-900 truncate min-w-0">{k.kova}</span>
                <span className="flex items-center gap-3 shrink-0 tnum">
                  <span className="text-ink-500">{fmtNum(k.parti_sayisi)} parti</span>
                  <span className="text-ink-900 font-medium">{fmtTRY(k.kalan_deger)}</span>
                </span>
              </>
            );
            return (
              <li key={k.kova}>
                {ac ? (
                  <button
                    type="button"
                    data-testid={`kova-${k.kova}`}
                    onClick={() => ac({ tur: 'kova', kova: k.kova })}
                    className="focus-ring w-full flex items-center justify-between gap-3 text-[13px] hover:underline underline-offset-2 text-left"
                  >
                    {icerik}
                  </button>
                ) : (
                  <div data-testid={`kova-${k.kova}`} className="flex items-center justify-between gap-3 text-[13px]">
                    {icerik}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 2 — Ölü stok (990064) — ANLIK, parametresiz
// ════════════════════════════════════════════════════════════════════════════════════════════

interface OluStokSatiri { stok_kod: string; stok_ad: string; kategori: string; eldeki: number; deger: number }

const OLU_STOK_BILGI = 'Bugün stoğu olan ve 90 gündür hiç satmayan ürünler.';

function OluStokKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  const q = useStokSorgu<OluStokSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-olu-stok-iskelet" baslik="Ölü stok" yukseklik={140} satir={2} />;
  }
  if (q.isError) {
    return <KartHatasi testid="sermaye-olu-stok-hata" baslik="Ölü stok" yukseklik={140} mesaj="Ölü stok yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data;
  const toplam = satirlar.reduce((t, s) => t + (Number(s.deger) || 0), 0);

  return (
    <div data-testid="sermaye-olu-stok" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col justify-center"
        title="Ölü stok"
        actions={<Bilgi testid="sermaye-olu-stok-bilgi" metin={OLU_STOK_BILGI} />}
      >
        <p className="text-[24px] font-semibold tnum text-ink-900">{fmtNum(satirlar.length)} ürün</p>
        <p className="text-[14px] tnum text-ink-500 mt-1">{fmtTRY(toplam)}</p>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 3 — ABC Analizi (990045) — A/B/C + D (net ciro ≤ 0)
// ════════════════════════════════════════════════════════════════════════════════════════════

interface AbcSatiri { sinif: string; urun_sayisi: number; ciro: number; urun_pay: number; ciro_pay: number }

const ABC_D_BILGI = 'Dönem net cirosu sıfır ya da negatif (iade ağırlıklı) ürünler — standart ABC sözlüğünde yoktur.';

/**
 * I-5 (final-fix F-3) — `135-gizova-turetilmis-olculer.sql:9-20` şerhi: bu ciro FİŞ değil SATIR
 * tanesinde (`d.dahil_tutar`, KDV DAHİL), üstüne yalnız `stok_cinsi = 1` (ürün kartları — ambalaj/
 * depozito gibi ürün-dışı kalemler dışlanır). Kart taban/tane bilgisini kendi göstermezse kullanıcı
 * bu toplamdan fire/ciro oranı türetmeye kalkarsa (fire farklı bir tabanda) şeritteki oranı tutturamaz.
 */
const ABC_CIRO_BILGI = 'Ciro KDV dahil, satış SATIRI (dahil_tutar) tabanında; yalnız stok_cinsi=1 (ürün kartları, ambalaj/depozito hariç).';

function AbcKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  const q = useStokSorgu<AbcSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-abc-iskelet" baslik="ABC" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return <KartHatasi testid="sermaye-abc-hata" baslik="ABC Analizi" yukseklik={200} mesaj="ABC analizi yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data;
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="sermaye-abc" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="ABC Analizi"
        subtitle="Ciroya göre kümülatif pay — A %80 / B %95 / C kalan"
        actions={<Bilgi testid="sermaye-abc-ciro-bilgi" metin={ABC_CIRO_BILGI} />}
      >
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="text-[12px] text-ink-500 border-b border-border">
              <th className="text-left font-medium py-2 pr-3">Sınıf</th>
              <th className="text-right font-medium py-2 px-3">Ürün</th>
              <th className="text-right font-medium py-2 px-3">Ciro</th>
              <th className="text-right font-medium py-2 pl-3">Ciro payı</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => (
              <tr key={s.sinif} data-testid={`sermaye-abc-satir-${s.sinif}`} className="border-b border-border last:border-b-0">
                <td className="py-2 pr-3 text-ink-900 font-medium">
                  {s.sinif}
                  {s.sinif === 'D' && (
                    <span className="ml-1.5 inline-block align-middle">
                      <Bilgi testid="sermaye-abc-d-bilgi" metin={ABC_D_BILGI} />
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">
                  {fmtNum(s.urun_sayisi)}{' '}
                  <span className="text-ink-400">({oranMetni(s.urun_pay, 1)})</span>
                </td>
                <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(s.ciro)}</td>
                <td className="py-2 pl-3 text-right tnum text-ink-900 font-medium whitespace-nowrap">
                  {oranMetni(s.ciro_pay, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 4 — GMROI (990049) — TiklaAc ile tembel; kategoriye Σpay/Σpayda
// ════════════════════════════════════════════════════════════════════════════════════════════

interface GmroiSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  brut_kar: number;
  ort_envanter_deger: number;
  gmroi: number;
  etkin_gun: number;
}

const GMROI_ADET = 3;

/**
 * En düşük/en yüksek N kategori, ÇAKIŞMASIZ. Kategori sayısı 2N'den azsa (ör. 2 kategoride N=3)
 * her iki liste de N kadar isteseydi AYNI kategoriler iki listede birden görünürdü (ya da tek liste
 * tümünü yutup öbürünü boş bırakırdı) — bu yüzden `adet` en fazla toplamın YARISI kadardır, iki
 * liste ayrık kalır. Tek kategori varsa "en yüksek" boş kalır (karşılaştıracak ikinci yok).
 */
function enDusukEnYuksek(kategoriler: { kategori: string; oran: number | null }[], n = GMROI_ADET) {
  const gecerli = kategoriler
    .filter((k): k is { kategori: string; oran: number } => k.oran !== null)
    .sort((a, b) => a.oran - b.oran);
  const toplam = gecerli.length;
  if (toplam <= 1) return { dusuk: gecerli, yuksek: [] };
  const adet = Math.min(n, Math.floor(toplam / 2));
  const dusuk = gecerli.slice(0, adet);
  const yuksek = gecerli.slice(toplam - adet).reverse();
  return { dusuk, yuksek };
}

function GmroiIcerik({ kaynak, f }: { kaynak: StokKaynak; f: StokSorguFiltresi }) {
  const q = useStokSorgu<GmroiSatiri>(kaynak, f);
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-gmroi-iskelet" yukseklik={160} satir={4} />;
  }
  if (q.isError) {
    return <KartHatasi testid="sermaye-gmroi-hata" yukseklik={160} mesaj="GMROI yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data.map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return null;

  const kategoriler = kategoriyeTopla(satirlar, 'brut_kar', 'ort_envanter_deger');
  const { dusuk, yuksek } = enDusukEnYuksek(kategoriler);
  if (dusuk.length === 0 && yuksek.length === 0) return null;

  const Liste = ({ testidOn, baslik, satirlar: veri }: { testidOn: string; baslik: string; satirlar: { kategori: string; oran: number }[] }) => (
    <div className="flex-1 min-w-0">
      <h3 className="text-[12px] font-medium text-ink-500 mb-1.5">{baslik}</h3>
      <ul className="flex flex-col gap-1.5">
        {veri.map((k) => (
          <li
            key={k.kategori}
            data-testid={`sermaye-gmroi-${testidOn}-${k.kategori}`}
            className="flex items-center justify-between gap-3 text-[13px]"
          >
            <span className="text-ink-900 truncate min-w-0">{k.kategori}</span>
            <span className="text-ink-700 tnum shrink-0">{fmtCarpan(k.oran)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="p-5 flex flex-col sm:flex-row gap-5">
      <Liste testidOn="dusuk" baslik="En düşük" satirlar={dusuk} />
      <Liste testidOn="yuksek" baslik="En yüksek" satirlar={yuksek} />
    </div>
  );
}

// Not: metin bilerek "GMROI" kelimesini İÇERMEZ — TiklaAc başlığındaki görünür "GMROI" metniyle
// metin sorgusunda (`getByText(/GMROI/)`) çakışmasın (Bilgi'nin açıklaması DOM'da hep var, sr-only değil).
const GMROI_BILGI = 'Brüt kâr / ortalama envanter maliyeti. Yalnız dönemde satışı olan ürünleri kapsar.';

function GmroiKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  if (!kaynak) return null;
  return (
    <div data-testid="sermaye-gmroi" className="h-full">
      <SectionCard className="h-full flex flex-col" bodyClass="p-0 flex-1 flex flex-col">
        <TiklaAc
          anahtar="sermaye-gmroi"
          baslik="GMROI"
          altBaslik="Kategoriye göre en düşük ve en yüksek"
          bilgi={<Bilgi testid="sermaye-gmroi-bilgi" metin={GMROI_BILGI} />}
        >
          <GmroiIcerik kaynak={kaynak} f={f} />
        </TiklaAc>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Blok — SIKIŞAN SERMAYE (§4.3)
// ════════════════════════════════════════════════════════════════════════════════════════════

const KAPSAM_NOT_METNI = 'Şube süzgeci bu blokta geçerli değil — ölçüler tüm şubeleri kapsar.';

/**
 * Spec §4.3 — dört kart: Yaşlandırma / Ölü stok (§3.7 uyarısı: BİLEREK yan yana — 990048/990049
 * dönemde satışı olmayan ürünü göstermez, ölü stok yalnız 990064'te görünür) / ABC (+D) / GMROI
 * (TiklaAc ile tembel).
 *
 * F-5 (final-fix, 2026-09-16) — S1'in sabit `sube !== null` koşulu `kapsamNotuGorunsunMu` ile
 * DEĞİŞTİRİLDİ: not artık bu bloğun dört ds'inin (990043/990045/990049/990064) GERÇEK `@LOKASYON`
 * bildirimine bakar (`kapsam.ts`). Bugün hiçbiri bildirmiyor, davranış AYNI kalır; ama bir ds ileride
 * `@LOKASYON` kazanırsa not kod değişikliği olmadan kendiliğinden doğru kalır.
 */
export function SermayeBlok({ kaynaklar, donem, sube = null, ac }: Props) {
  const f: StokSorguFiltresi = { donem, sube, kategori: null };

  const hicKaynakYok = !kaynaklar.yaslandirma && !kaynaklar.oluStok && !kaynaklar.abc && !kaynaklar.gmroi;
  if (hicKaynakYok) return null;

  const kapsamNotuGorunur = kapsamNotuGorunsunMu(
    [kaynaklar.yaslandirma, kaynaklar.oluStok, kaynaklar.abc, kaynaklar.gmroi],
    '@LOKASYON',
    sube,
  );

  return (
    <section data-testid="sermaye-blok" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-ink-900">
          Sıkışan sermaye
          {kapsamNotuGorunur && (
            <span data-testid="sermaye-kapsam-not" className="block text-[11px] font-normal text-ink-400 mt-0.5">
              {KAPSAM_NOT_METNI}
            </span>
          )}
        </h2>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <YaslandirmaKarti kaynak={kaynaklar.yaslandirma} f={f} ac={ac} />
        <OluStokKarti kaynak={kaynaklar.oluStok} f={f} />
        <AbcKarti kaynak={kaynaklar.abc} f={f} />
        <GmroiKarti kaynak={kaynaklar.gmroi} f={f} />
      </div>
    </section>
  );
}
